import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { PublicRoute } from '@/components/PublicRoute';
import { Layout } from '@/components/layout/Layout';
import { Toaster } from '@/components/ui/toaster';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle } from 'lucide-react';

// Lazy load pages
const HomePage = lazy(() => import('@/pages/HomePage').then((m) => ({ default: m.HomePage })));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const VerifyPage = lazy(() => import('@/pages/auth/VerifyPage').then((m) => ({ default: m.VerifyPage })));
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
);
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const PoolDetailsPage = lazy(() => import('@/pages/PoolDetailsPage').then((m) => ({ default: m.PoolDetailsPage })));
const CreatePoolPage = lazy(() => import('@/pages/CreatePoolPage').then((m) => ({ default: m.CreatePoolPage })));
const PickSubmissionPage = lazy(() =>
  import('@/pages/PickSubmissionPage').then((m) => ({ default: m.PickSubmissionPage }))
);
const PoolManagementPage = lazy(() =>
  import('@/pages/PoolManagementPage').then((m) => ({ default: m.PoolManagementPage }))
);
const CelebrityDetailsPage = lazy(() =>
  import('@/pages/CelebrityDetailsPage').then((m) => ({ default: m.CelebrityDetailsPage }))
);
const CelebritySearchPage = lazy(() =>
  import('@/pages/CelebritySearchPage').then((m) => ({ default: m.CelebritySearchPage }))
);
const AdminPage = lazy(() =>
  import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage }))
);
const MulliganWindowsAdminPage = lazy(() =>
  import('@/pages/MulliganWindowsAdminPage').then((m) => ({
    default: m.MulliganWindowsAdminPage,
  }))
);
const MulliganStatusPage = lazy(() =>
  import('@/pages/MulliganStatusPage').then((m) => ({
    default: m.MulliganStatusPage,
  }))
);

// Query client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Loading fallback
function PageLoader() {
  return (
    <div className="flex h-[50vh] items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

// Error fallback
function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
      <AlertCircle className="h-12 w-12 text-destructive" />
      <h2 className="text-xl font-semibold">Something went wrong</h2>
      <p className="text-muted-foreground">{error.message}</p>
      <Button onClick={resetErrorBoundary}>Try again</Button>
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route element={<Layout />}>
                  <Route path="/" element={<HomePage />} />

                  {/* Auth routes */}
                  <Route
                    path="/login"
                    element={
                      <PublicRoute>
                        <LoginPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/register"
                    element={
                      <PublicRoute>
                        <RegisterPage />
                      </PublicRoute>
                    }
                  />
                  <Route path="/verify" element={<VerifyPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />

                  {/* Protected routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pools/create"
                    element={
                      <ProtectedRoute>
                        <CreatePoolPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pools/:poolId"
                    element={
                      <ProtectedRoute>
                        <PoolDetailsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pools/:poolId/picks"
                    element={
                      <ProtectedRoute>
                        <PickSubmissionPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pools/:poolId/manage"
                    element={
                      <ProtectedRoute>
                        <PoolManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pools/:poolId/mulligan-admin"
                    element={
                      <ProtectedRoute requiresHost>
                        <MulliganWindowsAdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pools/:poolId/mulligan"
                    element={
                      <ProtectedRoute>
                        <MulliganStatusPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/celebrities/search"
                    element={
                      <ProtectedRoute>
                        <CelebritySearchPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/celebrities/:name"
                    element={
                      <ProtectedRoute>
                        <CelebrityDetailsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin route - protected and requires site admin check (handled in AdminPage) */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <AdminPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Catch all */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </Suspense>
            <Toaster />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
